import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BookingInfo } from "@/types/booking";

export const useSlotReservation = () => {
  const [isReserving, setIsReserving] = useState(false);
  const [reservationExpiry, setReservationExpiry] = useState<Date | null>(null);
  const { toast } = useToast();

  const reserveSlot = async (
    bookingInfo: BookingInfo,
    selectedDate: Date,
    selectedTime: string
  ): Promise<string | null> => {
    setIsReserving(true);

    try {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const startTime = new Date(selectedDate);
      startTime.setHours(hours, minutes, 0, 0);
      
      const endTime = new Date(startTime.getTime() + (bookingInfo.duration * 60000));

      const { data, error } = await supabase.functions.invoke('reserve-slot', {
        body: {
          practitionerId: bookingInfo.practitionerId,
          serviceId: bookingInfo.serviceId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          variant: "destructive",
          title: "Reservation Failed",
          description: data.error
        });
        return null;
      }

      setReservationExpiry(new Date(data.reservedUntil));
      
      return data.bookingId;
    } catch (error: any) {
      console.error('Slot reservation error:', error);
      toast({
        variant: "destructive",
        title: "Reservation Failed",
        description: error.message || "Unable to reserve this time slot"
      });
      return null;
    } finally {
      setIsReserving(false);
    }
  };

  const clearReservation = () => {
    setReservationExpiry(null);
  };

  return {
    isReserving,
    reservationExpiry,
    reserveSlot,
    clearReservation
  };
};
